import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';

import { ProductDataFilenameConventionService } from 'src/app/services/product-data-filename-conventions/product-data-filename-convention.service';
import {
  ProductDataFilenameConvention,
  ProductDataFilenameConventionWriteReason,
  WriteReasonKind,
  fileExtensionLabel,
  filenameIdentifierLabel,
  writeReasonLabel,
} from 'src/app/models/product-data-filename-conventions/product-data-filename-convention';

interface HistoryRow {
  readonly source: ProductDataFilenameConvention;
  readonly reasonKind: WriteReasonKind;
  readonly reasonLabel: string;
  readonly reasonDetail: string | null;
  readonly identifierLabel: string;
  readonly extensionsLabel: string;
  readonly createdAt: string;
  readonly createdByEmail: string;
}

// Read-only history route for an org's PDFC audit trail.
// Route: /organizations/:orgId/product-data-filename-conventions
@Component({
  selector: 'app-product-data-filename-convention-history',
  templateUrl: './product-data-filename-convention-history.component.html',
  styleUrls: ['./product-data-filename-convention-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class ProductDataFilenameConventionHistoryComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  organizationId: string | null = null;
  rows: HistoryRow[] = [];
  loaded = false;
  readonly displayedColumns: readonly string[] = [
    'when',
    'by',
    'identifier',
    'extensions',
    'reason',
  ];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly service: ProductDataFilenameConventionService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.organizationId = this.route.snapshot.paramMap.get('orgId');
    if (this.organizationId === null) {
      this.loaded = true;
      return;
    }
    this.service
      .list(this.organizationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((list: ProductDataFilenameConvention[]) => {
        this.rows = list.map((row: ProductDataFilenameConvention) => this.toDisplayRow(row));
        this.loaded = true;
        this.cdr.markForCheck();
      });
  }

  isLatest(index: number): boolean {
    return index === 0;
  }

  reasonPillClass(kind: WriteReasonKind): string {
    return `reason-pill reason-pill--${kind}`;
  }

  goBack(): void {
    if (this.organizationId === null) {
      this.router.navigate(['/organizations']);
      return;
    }
    this.router.navigate(['/organizations', this.organizationId, 'update']);
  }

  private toDisplayRow(row: ProductDataFilenameConvention): HistoryRow {
    const kind = row.writeReason.kind;
    return {
      source: row,
      reasonKind: kind,
      reasonLabel: writeReasonLabel(kind),
      reasonDetail: this.detailFor(row.writeReason),
      identifierLabel: filenameIdentifierLabel(row.filename.identifier),
      extensionsLabel: row.filename.extensions.map(fileExtensionLabel).join(', '),
      createdAt: row.createdAt,
      createdByEmail: row.createdByEmail ?? 'Unknown',
    };
  }

  private detailFor(writeReason: ProductDataFilenameConventionWriteReason): string | null {
    return writeReason.kind === 'other' ? writeReason.detail : null;
  }
}
